import React, { useContext } from 'react';
import { ArrowRight, Phone, ShieldCheck, Hammer, Calendar } from 'lucide-react';
import { QuickQuoteContext } from '../LandingPage';
import { useLandingContent, resolveImageUrl } from '../useLandingContent';

export default function Hero() {
  const openQuickQuote = useContext(QuickQuoteContext);
  const { content } = useLandingContent();
  const company = content.company;
  const hero = content.hero || {};
  const yearsExp = new Date().getFullYear() - (company.foundingYear || 2000);
  const statIcons = [Calendar, ShieldCheck, Hammer];

  const bgImage = resolveImageUrl(hero.background_image);
  const videoUrl = hero.video_url || '';
  const isYoutube = videoUrl && /youtube\.com|youtu\.be/.test(videoUrl);
  const isVimeo = videoUrl && /vimeo\.com/.test(videoUrl);
  const isDirectVideo = videoUrl && /\.(mp4|webm|ogg)$/i.test(videoUrl);

  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden bg-stone-950" data-testid="hero">
      {/* Background: video se fornito, altrimenti immagine */}
      <div className="absolute inset-0 z-0">
        {isDirectVideo ? (
          <video src={resolveImageUrl(videoUrl)} autoPlay muted loop playsInline
            className="w-full h-full object-cover opacity-40" />
        ) : isYoutube || isVimeo ? (
          <iframe
            src={`${isYoutube
              ? videoUrl.replace('watch?v=', 'embed/').split('&')[0] + '?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&modestbranding=1&playlist=' + (videoUrl.match(/(?:v=|youtu\.be\/)([^&]+)/)?.[1] || '')
              : videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/') + '?autoplay=1&muted=1&loop=1&background=1'}`}
            title="Background video"
            className="w-full h-full object-cover opacity-40 pointer-events-none"
            frameBorder="0" allow="autoplay; encrypted-media"
          />
        ) : (
          <img
            src={bgImage}
            alt={`Cantiere edile ${company.brand || ''} a ${company.address?.city || ''}`}
            className="w-full h-full object-cover opacity-40"
            loading="eager"
            fetchpriority="high"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-primary/10 border border-primary/30 rounded-full">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-[11px] font-bold text-primary uppercase tracking-widest">{hero.badge}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight mb-6">
            {hero.title_line1}<br />
            {hero.title_line2}<br />
            <span className="text-primary">{hero.title_line3}</span>
          </h1>

          <p className="text-lg sm:text-xl text-stone-300 max-w-2xl mb-8 leading-relaxed whitespace-pre-line">
            {hero.subtitle}
          </p>

          <div className="flex flex-wrap gap-3 mb-12">
            <button
              onClick={openQuickQuote}
              data-testid="hero-cta-preventivo"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white font-black text-base rounded-md hover:bg-primary transition-all active:scale-95 shadow-xl shadow-primary/30">
              {hero.cta_primary || 'Richiedi Preventivo Gratuito'} <ArrowRight size={18} />
            </button>
            <a href={`tel:${(company.phoneFisso || '').replace(/\s/g, '')}`} data-testid="hero-cta-tel"
              className="inline-flex items-center gap-2 px-6 py-3.5 border-2 border-white/30 text-white font-bold rounded-md hover:bg-white/10 transition-all">
              <Phone size={18} /> {company.phoneFisso}
            </a>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-4 sm:gap-6 max-w-2xl">
            {(hero.stats || []).slice(0, 3).map((s, i) => (
              <Badge key={i} icon={statIcons[i] || Hammer}
                value={s.value === 'auto' ? `${yearsExp}+` : s.value}
                label={s.label} />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-primary rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}

function Badge({ icon: Icon, value, label }) {
  return (
    <div className="border-l-2 border-primary/50 pl-4">
      <div className="flex items-center gap-1.5 text-primary mb-1"><Icon size={14} /></div>
      <p className="text-2xl sm:text-3xl font-black text-white">{value}</p>
      <p className="text-[11px] sm:text-xs text-stone-400 uppercase tracking-wider font-semibold">{label}</p>
    </div>
  );
}
